import * as Dialog from '@radix-ui/react-dialog';
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Check, Loader2, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/utils/cn';
import { getCategorizedLibraryAssets, importLibraryAssets, type CategorizedAssetItem, type LibraryCategory } from '@/api/modules/assets';
import { AssetImage } from '@/components/asset-image';

/** Tab key → categorized API category (all defaults to 'image'). */
const TABS: Array<{ key: string; label: string; category: LibraryCategory }> = [
  { key: 'all', label: '全部', category: 'image' },
  { key: 'image', label: '图片', category: 'image' },
  { key: 'animation', label: '动效', category: 'animation' },
  { key: 'audio', label: '音效', category: 'soundEffect' },
  { key: 'particle', label: '粒子', category: 'particle' },
  { key: 'bgm', label: 'BGM', category: 'bgm' },
];

const PAGE = 50;

interface AssetLibraryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sessionId?: string;
  onImported?: (assets: CategorizedAssetItem[]) => void;
  /** 单选模式：点击即选中唯一一项（替换场景用，再点取消）。 */
  single?: boolean;
  /** 确认时是否先导入会话资产库（默认 true；替换场景传 false 只回调 onPicked）。 */
  importOnConfirm?: boolean;
  /** 选中确认回调（替换场景入口，single 模式下通常只含 1 项）。 */
  onPicked?: (assets: CategorizedAssetItem[]) => void;
  /** 打开时定位的初始分类 tab（对应 TABS key）。 */
  initialTab?: string;
  /** 替换场景资源类型限制：只展示与目标类型相关的类目 tab（image→图片/动效/粒子，audio→音效/BGM）。 */
  assetKind?: 'image' | 'audio';
}

export function AssetLibraryModal({ open, onOpenChange, sessionId, onImported, single, importOnConfirm = true, onPicked, initialTab, assetKind }: AssetLibraryModalProps): JSX.Element {
  const [scope, setScope] = useState<'public' | 'personal'>('public');
  const [tab, setTab] = useState(initialTab ?? 'all');
  const [keyword, setKeyword] = useState('');
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<CategorizedAssetItem[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [total, setTotal] = useState(0);
  const [importing, setImporting] = useState(false);
  const [subcategory, setSubcategory] = useState('');
  const [subcategories, setSubcategories] = useState<string[]>([]);
  const offsetRef = useRef(0);
  const loadedRef = useRef(0);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 替换场景按资源类型限制类目 tab；未传 assetKind（通用场景）展示全部
  const visibleTabs = useMemo(() => {
    if (assetKind === 'audio') return TABS.filter((t) => t.key === 'audio' || t.key === 'bgm');
    if (assetKind === 'image') return TABS.filter((t) => ['all', 'image', 'animation', 'particle'].includes(t.key));
    return TABS;
  }, [assetKind]);

  const fetchLibrary = useCallback(async (t_tab: string, kw: string, sub: string, loadMore = false) => {
    setLoading(true);
    try {
      const offset = loadMore ? offsetRef.current : 0;
      const activeCategory = visibleTabs.find((t) => t.key === t_tab)?.category ?? 'image';
      const { items: newItems, total: newTotal, subcategories: newSubs } = await getCategorizedLibraryAssets({
        scope, category: activeCategory, subcategory: sub || undefined, search: kw, limit: PAGE, offset,
      });
      if (!loadMore) {
        setSubcategories(newSubs);
      }
      if (loadMore) {
        setItems((prev) => [...prev, ...newItems]);
        loadedRef.current += newItems.length;
      } else {
        setItems(newItems);
        loadedRef.current = newItems.length;
      }
      setTotal(newTotal);
      offsetRef.current = offset + newItems.length;
    } catch {
      if (!loadMore) { setItems([]); loadedRef.current = 0; setSubcategories([]); }
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [scope, visibleTabs]);

  useEffect(() => {
    if (open) {
      offsetRef.current = 0; loadedRef.current = 0;
      setSubcategory('');
      setKeyword('');
      // 只在打开时设置初始 tab；若 initialTab 被 assetKind 过滤掉则落到第一个可见 tab
      const initialKey = visibleTabs.some((t) => t.key === initialTab) ? initialTab : visibleTabs[0]?.key ?? 'all';
      setTab(initialKey);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, visibleTabs]);

  // tab/scope/搜索变化时重新拉取（不重置 initialTab，仅当 open 且 tab 变化时触发）
  useEffect(() => {
    if (open) {
      offsetRef.current = 0; loadedRef.current = 0;
      fetchLibrary(tab, keyword, '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tab, scope]);

  const handleSubcategoryChange = (sub: string): void => {
    setSubcategory(sub);
    offsetRef.current = 0; loadedRef.current = 0;
    fetchLibrary(tab, keyword, sub);
  };

  const handleSearch = (v: string): void => {
    setKeyword(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      offsetRef.current = 0; loadedRef.current = 0;
      fetchLibrary(tab, v, '');
    }, 300);
  };

  const toggle = (id: string): void => {
    if (single) {
      // 单选：点击即选中唯一一项，再点取消
      setSelected((prev) => (prev.includes(id) ? [] : [id]));
      return;
    }
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>): void => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 60 && !loading && loadedRef.current < total) {
      fetchLibrary(tab, keyword, '', true);
    }
  };

  const reset = (): void => { setSelected([]); setKeyword(''); };

  const handleConfirm = async (): Promise<void> => {
    // 选中态以 url 为唯一键（id 在旧 manifest 中曾因同名文件碰撞重复，url 必然唯一）
    const chosen = items.filter((a) => selected.includes(a.url));
    if (!chosen.length) return;
    if (sessionId && importOnConfirm) {
      setImporting(true);
      try {
        await importLibraryAssets(chosen.map((a) => a.id), sessionId);
        toast.success(`已导入 ${chosen.length} 个资源`);
      } catch {
        toast.error('导入失败');
        setImporting(false);
        return;
      }
      setImporting(false);
    }
    onImported?.(chosen);
    onPicked?.(chosen);
    reset();
    onOpenChange(false);
  };

  return (
    <Dialog.Root open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out" />
        <Dialog.Content className="glass-card fixed left-1/2 top-1/2 z-50 flex max-h-[80vh] w-full max-w-2xl -translate-x-1/2 -translate-y-1/2 flex-col p-5 data-[state=open]:animate-in data-[state=closed]:animate-out">
          <div className="mb-3 flex items-center justify-between">
            <Dialog.Title className="text-base font-semibold text-foreground">资产库</Dialog.Title>
            <Dialog.Close asChild><button className="icon-btn rounded p-1"><X size={16} /></button></Dialog.Close>
          </div>

          <div className="mb-2 flex items-center gap-1 rounded-lg bg-muted/40 p-0.5">
            {([['public', '公共资产库'], ['personal', '个人资产库']] as const).map(([key, label]) => (
              <button key={key} onClick={() => { setScope(key); setSubcategory(''); }}
                className={cn('flex-1 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors',
                  scope === key ? 'bg-primary/15 text-primary' : 'text-muted-foreground hover:text-foreground')}>
                {label}
              </button>
            ))}
          </div>

          <div className="mb-2 flex items-center gap-1">
            {visibleTabs.map((t) => (
              <button key={t.key} onClick={() => setTab(t.key)}
                className={cn('rounded-md px-2.5 py-1 text-xs transition-colors',
                  tab === t.key ? 'bg-primary/15 text-primary' : 'text-muted-foreground hover:text-foreground')}>
                {t.label}
              </button>
            ))}
          </div>

          {/* 二级类目：仅公共资产库展示 */}
          {scope === 'public' && subcategories.length > 0 && (
            <div className="mb-2 flex items-center gap-1 flex-wrap">
              <button onClick={() => handleSubcategoryChange('')}
                className={cn('rounded px-2 py-0.5 text-[11px] transition-colors',
                  !subcategory ? 'bg-neon-cyan/15 text-neon-cyan font-medium' : 'text-muted-foreground hover:text-foreground')}>
                全部
              </button>
              {subcategories.map((sub) => (
                <button key={sub} onClick={() => handleSubcategoryChange(sub)}
                  className={cn('rounded px-2 py-0.5 text-[11px] transition-colors',
                    subcategory === sub ? 'bg-neon-cyan/15 text-neon-cyan font-medium' : 'text-muted-foreground hover:text-foreground')}>
                  {sub}
                </button>
              ))}
            </div>
          )}

          <div className="relative mb-3">
            <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input className="input-neon w-full py-1.5 pl-7 pr-3 text-xs" placeholder="搜索资源..." value={keyword} onChange={(e) => handleSearch(e.target.value)} />
          </div>

          <div className="min-h-[220px] flex-1 overflow-y-auto" onScroll={handleScroll}>
            {loading && items.length === 0 ? (
              <div className="flex h-52 items-center justify-center"><Loader2 size={22} className="animate-spin text-neon-cyan" /></div>
            ) : items.length === 0 ? (
              <div className="py-16 text-center text-xs text-muted-foreground">暂无资源</div>
            ) : (
              <div className="grid grid-cols-4 gap-2.5">
                {items.map((a) => {
                  // url 作唯一键：同名不同扩展名/全角字符的资产 id 可能相同，避免联动选中
                  const isSel = selected.includes(a.url);
                  return (
                    <button key={a.url} onClick={() => toggle(a.url)}
                      className={cn('group relative overflow-hidden rounded-lg border-2 text-left transition-colors',
                        isSel ? 'border-primary bg-primary/5' : 'border-transparent hover:border-border/60')}>
                      <div className="absolute left-1.5 top-1.5 z-10">
                        <span className={cn('flex h-4 w-4 items-center justify-center rounded border',
                          isSel ? 'border-primary bg-primary text-white' : 'border-white/70 bg-black/30')}>
                          {isSel && <Check size={11} />}
                        </span>
                      </div>
                      <AssetImage src={a.previewUrl ?? a.url} alt={a.name} className="aspect-square w-full" />
                      <p className="truncate px-1.5 py-1 text-center text-[10px] text-muted-foreground">{a.name || '未命名'}</p>
                    </button>
                  );
                })}
              </div>
            )}
            {loading && items.length > 0 && (
              <div className="flex justify-center py-3"><Loader2 size={16} className="animate-spin text-neon-cyan/60" /></div>
            )}
          </div>

          <div className="mt-3 flex items-center justify-between border-t border-border/30 pt-3">
            <span className="text-xs text-muted-foreground">
              已选 <span className="neon-text font-semibold">{selected.length}</span> 项 · 共 {total} 个资源
            </span>
            <div className="flex gap-2">
              <Dialog.Close asChild><button className="rounded-lg border border-border/50 px-3 py-1.5 text-xs text-muted-foreground hover:bg-muted/30">取消</button></Dialog.Close>
              <button onClick={handleConfirm} disabled={!selected.length || importing}
                className="glow-btn flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium disabled:opacity-40">
                {importing && <Loader2 size={13} className="animate-spin" />}{single ? '确认使用' : '确认添加'}
              </button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
